import { ConvertToRestDayModal } from '@client/new/pages/workout/components/ConvertToRestDayModal/ConvertToRestDayModal';
import { ConvertToRestDayToast } from '@client/new/pages/workout/components/ConvertToRestDayModal/ConvertToRestDayToast';
import { Program } from '@playbook-technologies/common-enums';
import auth from '@client/components/auth';
import { programUrl as getProgramUrl } from '@client/new/helpers/landingPages';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { AnyFn } from '../../../../new/helpers/types';
import { Link } from 'react-router';
import { Button } from '@mui/material';
import { SmartTable } from '@client/new/components/SmartTable/SmartTable';
import { WorkoutLinkModal } from '@client/new/pages/collection/workouts/WorkoutLinkModal';
import { DuplicateWorkoutDialog } from '@client/new/features/workout-duplicate';
import {
  ProgramWorkoutsProps,
  WorkoutProgram,
  useProgramWorkouts,
} from './useProgramWorkouts';
import * as styles from './workouts.css';

type ProgramSectionProps = {
  program: WorkoutProgram;
  onReorder: (programId: number, workoutIds: number[]) => Promise<unknown>;
  influencerId: number;
  influencer: {
    customURL: string;
  };
  onChange?: AnyFn;
};
export const ProgramSection = (props: ProgramSectionProps) => {
  const programName = props.program.isVisible
    ? props.program.name
    : `${props.program.name}* (Not live)`;

  const typeOfProgram = Program.Type.fromValue(props.program.type);
  const url = `/admin/${props.influencerId}/${
    typeOfProgram === Program.Type.Program ? 'program' : 'category'
  }/${props.program.id}/edit#about`;

  const isLocked =
    !(auth.isAdministrator() || auth.isModerator()) && props.program.isLocked;

  const displayLandingPageButtons =
    props.program.accessType === Program.AccessType.Premium.value ||
    (props.program.accessType === Program.AccessType.Exclusive.value &&
      props.program.exclusiveProgramMetadata?.price != null);

  const programUrl = getProgramUrl(props.influencer, props.program);
  return (
    <section className="programs">
      <h2
        className={styles.programHeader}
        style={{ borderColor: `#${props.program.color}` }}
      >
        <span style={{ flex: 1, lineHeight: '2.1rem' }}>
          {programName} {isLocked && `(Read-only)`}
        </span>
        <div className={styles.buttonsWrapper}>
          {displayLandingPageButtons && (
            <Link
              className="dropdown-row"
              to={url.replace('#about', '#sales-page')}
            >
              <Button variant="outlined" className={styles.programEditButton}>
                Edit Landing Page
              </Button>
            </Link>
          )}
          {displayLandingPageButtons && (
            <Link
              className="dropdown-row"
              href={programUrl}
              target="_blank"
              to=""
            >
              <Button variant="outlined" className={styles.programEditButton}>
                Open Landing Page
                <Icon className="ml-1" icon={faExternalLink} />
              </Button>
            </Link>
          )}
          {props.program.id !== 0 && (
            <Link className="dropdown-row" to={url}>
              <Button variant="outlined" className={styles.programEditButton}>
                {isLocked ? 'View' : 'Edit'}
              </Button>
            </Link>
          )}
        </div>
      </h2>
      <ProgramWorkouts
        program={props.program}
        influencerId={props.influencerId}
        onReorder={props.onReorder}
        onChange={props.onChange}
      />
    </section>
  );
};

const ProgramWorkouts = (props: ProgramWorkoutsProps) => {
  const vm = useProgramWorkouts(props);

  const renderTable = () => {
    if (vm.isEmpty)
      return (
        <p>
          <br />
          No workouts in program.
        </p>
      );

    if (vm.table.type === 'single') return <SmartTable {...vm.table.props} />;

    return (
      <>
        <p>
          Members see the first {vm.table.limit} workouts.{' '}
          <a className="cursor-pointer" href={vm.table.editUrl}>
            Edit
          </a>
        </p>
        <SmartTable {...vm.table.props} />
      </>
    );
  };

  return (
    <div className="mt-5">
      {renderTable()}
      <ConvertToRestDayModal
        isOpen={vm.convertToRestDay.isOpen}
        workoutTitle={vm.convertToRestDay.workoutTitle}
        onCancel={vm.convertToRestDay.cancel}
        onConfirm={vm.convertToRestDay.confirm}
      />
      {vm.convertToRestDay.outcome ? (
        <ConvertToRestDayToast
          outcome={vm.convertToRestDay.outcome}
          onShown={vm.convertToRestDay.clearOutcome}
        />
      ) : null}
      <WorkoutLinkModal
        isOpen={vm.linkModal.isOpen}
        url={vm.linkModal.url}
        onClose={vm.linkModal.onClose}
      />
      {vm.duplicateDialog ? (
        <DuplicateWorkoutDialog {...vm.duplicateDialog} />
      ) : null}
    </div>
  );
};
